import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import {
  Control,
  Controller,
  FieldPath,
  FieldValues,
  PathValue,
  useFormContext,
} from 'react-hook-form';
import ReactSelect, {
  components,
  CSSObjectWithLabel,
  FormatOptionLabelMeta,
  GroupBase,
  InputProps,
  OptionsOrGroups,
} from 'react-select';
import { FormFeedback } from '../FormFeedback';
import { FormField } from '../FormField';
import type { FormTypography } from '../FormLabel';
import { getOptionFieldError } from '../formUtils';
import { FormFieldProps } from '../interfaces';

// [MCV-1294] Shared portal menu props for react-select-based wrappers.
// When the Select is rendered inside a modal/dialog with `overflow-hidden`
// or a lower stacking context, the default menu gets clipped. Passing these
// props re-parents the menu to document.body using `fixed` positioning so it
// floats above ancestor clip boundaries. `unstyled` strips base menuPortal
// styles, so we rebuild position/top/left/width/zIndex from the props
// react-select passes as the second argument to the styles callback.
type MenuPortalArgs = {
  rect: { left: number; width: number };
  offset: number;
  position: string;
};
export const selectPortalProps = {
  menuPortalTarget: typeof document === 'undefined' ? undefined : document.body,
  menuPosition: 'fixed' as const,
  menuPortalStyle: (
    _base: CSSObjectWithLabel,
    { rect, offset, position }: MenuPortalArgs
  ): CSSObjectWithLabel => ({
    position: position as 'fixed' | 'absolute',
    top: offset,
    left: rect.left,
    width: rect.width,
    // [MCV-1536] Must clear every FormDialog tier (Z_INDEX_CLASSES in
    // components/dialogs/FormDialog.tsx: default z-10 / nested z-[70] / top z-[80])
    // and stay under Tooltip's z-[90]. The menu is portaled to document.body, so it
    // carries no stacking context of its own and competes with the dialog at the
    // root — at z-50 it painted *behind* the modal and its backdrop, making every
    // select inside the Add-Facility dialog invisible and unclickable. Same defect
    // Tooltip.tsx:157 already fixed for its own panel.
    zIndex: 85,
  }),
};

export const SelectStyles = (
  isFlagged?: boolean,
  isInvalid?: boolean,
  typography: FormTypography = 'default',
  wrapValueOnMobile = false
) => {
  // `text-base` resolves to our background color token, not a font size.
  const bodyTypography =
    typography === 'comfortable'
      ? 'text-[1rem] font-normal leading-normal text-content-primary'
      : 'body-text';
  return {
    // Profile opts in without changing desktop or other callers' truncation.
    ...(wrapValueOnMobile && {
      singleValue: () => 'whitespace-normal! [overflow-wrap:anywhere] sm:whitespace-nowrap!',
    }),
    // `unstyled` strips react-select's default inline styles including structural
    // ones (position, width, z-index). We must re-add them as Tailwind classes so
    // the menu floats over surrounding content instead of being constrained to the
    // parent container's box.
    menu: () =>
      classNames(
        'absolute w-full z-[1] bg-white border border-grey-800 rounded-md mt-2 py-2 overflow-hidden',
        bodyTypography
      ),
    menuList: () => classNames('max-h-[300px] overflow-y-auto overscroll-contain relative'),
    control: ({ isFocused, isDisabled }: { isFocused: boolean; isDisabled: boolean }) =>
      classNames(
        'border rounded-md py-2 px-3 w-full max-w-full overflow-hidden',
        typography === 'comfortable' && bodyTypography,
        'transition-colors duration-150',
        // [MCV-1478] react-select's Emotion styles emit `cursor: default`
        // UNCONDITIONALLY for the control (it's in the always-applied half of
        // the style object, and `unstyled` only strips background/color/padding
        // — not cursor). Emotion injects that rule UNLAYERED, while every
        // Tailwind utility here lives inside `@layer utilities`. Per CSS Cascade
        // level 5, importance is compared BEFORE layers, and an unlayered normal
        // declaration always outranks a layered one regardless of specificity —
        // so a bare `cursor-pointer`/`cursor-not-allowed` can never win here, no
        // matter how it's targeted. What defeats Emotion's unlayered rule is
        // IMPORTANCE, which IS compared before layers — not the position of the
        // `!`: Tailwind v4 accepts `!important` marked either as a trailing
        // `cursor-pointer!` or a leading `!cursor-pointer`, and both compile to
        // an `!important` declaration inside `@layer utilities` (verified
        // against the installed tailwindcss 4.1.17 compiler), so either spelling
        // fixes this. We use the trailing form as the preferred v4 spelling.
        // (Same reason `transition-colors` two lines up is silently inert here,
        // while `hover:bg-grey-900` below works fine — `unstyled` genuinely
        // strips its competing background rule.)
        isDisabled ? 'cursor-not-allowed!' : 'cursor-pointer!',
        // [MCV-1478] A searchable Select renders a real text <input> inside the
        // control whose native `cursor: text` overrides the control's pointer,
        // so hovering the value/search area still showed an I-beam. Force the
        // inner input to match the control cursor.
        isDisabled ? '[&_input]:cursor-not-allowed' : '[&_input]:cursor-pointer',
        isDisabled
          ? 'bg-grey-900 border-grey-800'
          : isInvalid
            ? 'bg-grey-950 border-error-50 ring-3 ring-error-50/8'
            : isFlagged
              ? classNames('bg-warning-50 border-warning', isFocused && 'ring-3 ring-warning/12')
              : classNames(
                  'bg-grey-950 border-grey-800',
                  isFocused && 'border-primary-50 shadow-[0_0_0_3px_rgba(24,94,200,0.12)]'
                )
      ),
    option: ({ isFocused, isDisabled }: { isFocused: boolean; isDisabled: boolean }) =>
      classNames(
        // [MCV-1478] Same unlayered-Emotion-rule problem as the control above:
        // react-select emits `cursor: default` unconditionally for the option
        // too, unaffected by `unstyled`, and beats any non-important Tailwind
        // utility regardless of specificity. The trailing `!` forces
        // `!important` (either spelling, leading or trailing, would — see the
        // control block above), which is compared before layers. `hover:bg-grey-900`
        // needs no `!` because `unstyled` genuinely strips the background rule
        // it competes with — only cursor is exempt from that stripping.
        'text-grey-100 pl-2 py-2.5 overflow-visible',
        isDisabled ? 'cursor-not-allowed!' : 'cursor-pointer! hover:bg-grey-900',
        isFocused ? 'bg-grey-900' : 'bg-white'
      ),
    noOptionsMessage: () => classNames(bodyTypography, 'overflow-visible py-2'),
    loadingMessage: () => classNames(bodyTypography, 'overflow-visible py-2'),
    ...(typography === 'comfortable' && {
      placeholder: () => 'text-content-secondary',
      dropdownIndicator: () => 'text-content-secondary',
      clearIndicator: () => 'text-content-secondary',
      loadingIndicator: () => 'text-content-secondary',
    }),
  };
};

/**
 * react-select replaces a caller's aria-describedby with its placeholder or
 * live-region ID. Restore the feedback ID supplied by our wrapper so the real
 * focusable input announces helper and validation copy consistently with the
 * native controls.
 */
export const FormSelectInput = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: InputProps<Option, IsMulti, Group>
) => {
  const selectProps = props.selectProps as typeof props.selectProps & {
    'aria-describedby'?: string;
    'aria-required'?: boolean;
  };

  return (
    <components.Input
      {...props}
      aria-describedby={selectProps['aria-describedby'] ?? props['aria-describedby']}
      aria-required={selectProps['aria-required'] || undefined}
    />
  );
};

interface Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends FormFieldProps<TFieldValues, TName> {
  control?: Control<TFieldValues>;
  /** Wrap the selected label below sm; retain the desktop single-line value. */
  wrapValueOnMobile?: boolean;
  onChange?: (e: unknown) => unknown;
  isSearchable?: boolean;
  isLoading?: boolean;
  noOptionsMessage?: string;
  // [MCV-1628] Render the dropdown menu in a portal so it escapes ancestor
  // overflow/clip boundaries (e.g. FormDialog's `overflow-y-auto` body).
  // Defaults to true: an in-flow menu is positioned `absolute` inside that
  // scroll container, so it is both clipped by it and counted in its
  // scrollable overflow -- which is what put a ~300px phantom scrollbar on
  // every dialog holding a non-portaled select. Pass `usePortal={false}`
  // only for a menu that must stay in normal document flow.
  usePortal?: boolean;
  options: readonly (PathValue<TFieldValues, TName> | GroupBase<PathValue<TFieldValues, TName>>)[];
  // [MCV-1664] For a Select with no visible `label` (e.g. a compact toolbar
  // control), this is the only way to give the underlying input an
  // accessible name -- FormField/FormLabel render nothing when `label` is
  // omitted.
  ariaLabel?: string;
  // [MCV-1761] Custom rendering for an option, e.g. a muted hint beside its label in the
  // menu. `meta.context` is 'menu' for a listed option and 'value' for the selected one.
  // Omitted, react-select renders the plain label and nothing changes. Filtering still
  // goes by the option's label.
  formatOptionLabel?: (
    option: PathValue<TFieldValues, TName>,
    meta: FormatOptionLabelMeta<PathValue<TFieldValues, TName>>
  ) => ReactNode;
  // [MCV-1802] The id of an error rendered OUTSIDE this field (a rule across fields that the form
  // resolver does not own), which makes the control invalid and points to that message, as its
  // own error would. The field's own error, when it has one, still wins.
  externalErrorId?: string;
}
export const Select = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: Props<TFieldValues, TName>
) => {
  const {
    name,
    cyTag,
    label,
    description,
    options,
    disabled,
    placeholder = 'Select...',
    isSearchable,
    isLoading,
    noOptionsMessage,
    tooltip,
    onChange,
    required,
    flagged,
    usePortal = true,
    errorCyTag,
    reserveFeedbackSpace,
    ariaLabel,
    formatOptionLabel,
    externalErrorId,
    wrapValueOnMobile = false,
  } = props;
  const {
    formState: { errors },
  } = useFormContext();
  const error = getOptionFieldError(errors, name);
  const errorId = `${name}-error`;
  const helperId = `${name}-helper`;
  const resolvedErrorCyTag = errorCyTag ?? (cyTag ? `${cyTag}-error` : undefined);
  const messageId = error?.message ? errorId : externalErrorId;
  return (
    <Controller
      name={name}
      render={({ field }) => {
        return (
          <FormField
            id={name}
            label={label}
            tooltip={tooltip}
            required={required}
            feedback={
              <FormFeedback
                error={error?.message}
                errorId={errorId}
                errorCyTag={resolvedErrorCyTag}
                description={description}
                helperId={helperId}
                reserveSpace={reserveFeedbackSpace}
              />
            }
          >
            <div data-cy={cyTag}>
              <ReactSelect
                {...field}
                inputId={name}
                aria-label={ariaLabel}
                value={field?.value?.label ? field.value : null}
                unstyled
                isDisabled={disabled}
                isSearchable={isSearchable}
                isLoading={isLoading}
                noOptionsMessage={noOptionsMessage ? () => noOptionsMessage : undefined}
                formatOptionLabel={formatOptionLabel}
                aria-invalid={!!error || !!externalErrorId || undefined}
                aria-required={required || undefined}
                aria-errormessage={messageId}
                aria-describedby={messageId ?? (description ? helperId : undefined)}
                classNames={SelectStyles(
                  flagged,
                  !!error || !!externalErrorId,
                  'default',
                  wrapValueOnMobile
                )}
                onChange={(option) => {
                  onChange?.(option);
                  field.onChange(option);
                }}
                menuPortalTarget={usePortal ? selectPortalProps.menuPortalTarget : undefined}
                menuPosition={usePortal ? selectPortalProps.menuPosition : undefined}
                styles={{
                  container: (base) => ({
                    ...base,
                    width: '100%',
                    maxWidth: '100%',
                  }),
                  singleValue: (base) => ({
                    ...base,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                  }),
                  ...(usePortal && { menuPortal: selectPortalProps.menuPortalStyle }),
                }}
                placeholder={<div className="body-text text-grey-700">{placeholder}</div>}
                components={{
                  Input: FormSelectInput,
                  DropdownIndicator: (props) => {
                    return (
                      <div>
                        {props.selectProps.menuIsOpen ? (
                          <ChevronUpIcon className="h-5 w-5 text-grey-100" />
                        ) : (
                          <ChevronDownIcon className="h-5 w-5 text-grey-100" />
                        )}
                      </div>
                    );
                  },
                }}
                options={
                  options as OptionsOrGroups<
                    PathValue<TFieldValues, TName>,
                    GroupBase<PathValue<TFieldValues, TName>>
                  >
                }
              />
            </div>
          </FormField>
        );
      }}
    />
  );
};
